//Proofed
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DescriptionIcon from '@mui/icons-material/Description'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import GppGoodIcon from '@mui/icons-material/GppGood'
import SpeedIcon from '@mui/icons-material/Speed'
import TuneIcon from '@mui/icons-material/Tune'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import type { HowItWorksStep } from '../components/HowItWorksSection'
import type { StatItem } from '../components/StatsSection'

export const tenderNinjaFeatures = [
    'Drafts responses from your own knowledge base',
    'Storyboards structure complex answers for higher scores',
    'Source tracing every sentence backed by evidence, no hallucionations',
    'Built in scoring to improve your responses, win more often',
]

export const ninjaHIWTitle = "How Tender Ninja works"
export const ninjaHIWSubtitle = "Three steps from document upload to a submission ready bid"
export const ninjaSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: UploadFileIcon,
        title: 'Upload the Tender Document',
        body: 'Upload your tender documents. Tender Ninja scans the ITT, PQQ, or RFP, identifies every question that needs answering and presents them in a structured, editable format',
    },
    {
        num: '2',
        Icon: AutoAwesomeIcon,
        title: 'Generate Draft Responses',
        body: 'AI drafts a response for every question drawing directly from your knowledge base. Storyboards structure complex answers for higher-scoring more persuasive responses',
    },
    {
        num: '3',
        Icon: SpeedIcon,
        title: 'Score & Refine',
        body: "Built-in scoring evaluates each response against an estimated evaluation criteria. Identify gaps, strengthen win themes and export your bid when it's submission ready",
    },
]

export const ninjaStats: StatItem[] = [
    { value: '5 mins', label: 'to generate your first draft' },
    { value: '100%', label: 'Responses grounded in your own documents' },
    { value: 'Zero', label: 'Hallucinations source tracing prevents them' },
    { value: '5.0 ★', label: 'Reviews.io rating · 52 reviews' },
]

export const ninjaFeatures = [
    {
        Icon: UploadFileIcon,
        title: 'Document Upload & Question Extraction',
        body: 'Upload any tender document (ITT, PQQ, RFP). Tender Ninja scans it, identifies all questions and presents them in an editable format no manual copy-paste required',
    },
    {
        Icon: DescriptionIcon,
        title: 'Knowledge Base Drafting',
        body: 'Every response is grounded in your own documents. Tender Ninja searches your knowledge base for relevant sources and builds answers from your actual company content',
    },
    {
        Icon: AutoFixHighIcon,
        title: 'Storyboard Structure',
        body: "For complex questions, storyboards give your response a clear narrative before drafting begins. Answer quality is consistently higher when a storyboard is used first",
    },
    {
        Icon: GppGoodIcon,
        title: 'Source Traceability',
        body: 'Every sentence in a generated response is traced back to its source. Weak attribution is flagged so you always know how each claim is supported before you submit',
    },
    {
        Icon: SpeedIcon,
        title: 'Built-in Answer Scoring',
        body: 'An automatic rubric evaluates each response against what an ideal answer should include. See estimated scores and identify weak sections before the buyer does',
    },
    {
        Icon: TuneIcon,
        title: 'Custom Scoring Templates',
        body: "Define your own scoring criteria and save reusable templates. Assess answers against your organisation's standards in addition to the built-in evaluation rubric",
    },
    {
        Icon: TextFieldsIcon,
        title: 'Word Wizard',
        body: "Responses that run long or short? Word Wizard refines draft length to match the tender's exact word count requirements without losing quality",
    },
    {
        Icon: FileDownloadIcon,
        title: 'Export & Collaborate',
        body: 'Export your full bid to support internal review, approval workflows and collaboration outside the platform. Keep your team aligned through final submission',
    },
]


export const ninjaFaqs = [
    {
        q: 'How does Tender Ninja generate responses?',
        a: 'You upload your tender document and Tender Ninja extracts all the questions. For each question, it searches your knowledge base for relevant source material, builds an optional storyboard structure and drafts a grounded response all in the background while you review earlier answers',
    },
    {
        q: 'What is a storyboard and do I need one?',
        a: "A storyboard is a structured outline for your response, built from your source documents before the full answer is drafted. It's optional, but for complex questions we recommend it answer quality is consistently higher when a storyboard is used first",
    },
    // {
    //     q: 'What documents can I upload?',
    //     a: 'Tender Ninja accepts any tender format ITTs, PQQs, RFPs and standard specification documents. The system identifies questions automatically and you can manually merge, split, or correct question boundaries before generating responses',
    // },

    {
        q: 'How does source tracing prevent hallucinations?',
        a: 'Every sentence in a generated response is linked back to its source document. The platform highlights any statements with weak attribution so you can review them before submitting. This ensures everything in your bid can be traced to real evidence',
    },
    {
        q: 'How does the built-in scoring work?',
        a: 'Tender Ninja automatically generates a scoring rubric based on what an ideal response should include. It evaluates your draft against that rubric on a pass/fail basis, with a reason for each checkpoint. You can also define your own criteria and save reusable scoring templates',
    },
    {
        q: 'Can multiple people collaborate on a bid?',
        a: 'Yes. Teams can collaborate on storyboards and responses within the platform and you can export the full bid at any point to support internal review, approval workflows, or editing outside the system',
    },
    // {
    //     q: 'Can I trial Tender Ninja before committing?',
    //     a: "Yes you get a full 14-day free trial with access to every feature. A credit card is required to activate the trial but you won't be charged until the trial period ends. Cancel anytime, no questions asked",
    // },
]

